import type {
    ChangePasswordRequestBody,
    CreateUserRequestBody,
    CreateUserResponse,
    GetUserListResponse,
    UpdateUserRequestBody,
    UpdateUserResponse,
    User,
} from '@engagement-web-app/common';
import { APIError } from './types';

export async function getCurrentUser(): Promise<User> {
    try {
        const res = await fetch('/api/users/me');
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export interface FetchUsersParams {
    from?: number;
    limit?: number;
    search?: string;
}

export async function fetchUsers(params: FetchUsersParams = {}): Promise<GetUserListResponse> {
    try {
        const searchParams = new URLSearchParams();
        if (params.from !== undefined) searchParams.set('from', String(params.from));
        if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
        if (params.search) searchParams.set('search', params.search);

        const queryString = searchParams.toString();
        const url = `/api/users${queryString ? `?${queryString}` : ''}`;

        const res = await fetch(url);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchUser(uuid: string): Promise<User> {
    try {
        const res = await fetch(`/api/users/${uuid}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function deleteUser(uuid: string): Promise<void> {
    try {
        const res = await fetch(`/api/users/${uuid}`, {
            method: 'DELETE',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function createUser(body: CreateUserRequestBody): Promise<CreateUserResponse> {
    try {
        const res = await fetch('/api/users', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function updateUser(uuid: string, body: UpdateUserRequestBody): Promise<UpdateUserResponse> {
    try {
        const res = await fetch(`/api/users/${uuid}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function changePassword(body: ChangePasswordRequestBody): Promise<void> {
    try {
        const res = await fetch('/api/users/me/password', {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function requestPasswordReset(): Promise<void> {
    try {
        const res = await fetch('/api/users/me/password-reset', {
            method: 'POST',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function resetUserPassword(uuid: string): Promise<void> {
    try {
        const res = await fetch(`/api/users/${uuid}/password-reset`, {
            method: 'POST',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function requestMfaReset(): Promise<void> {
    try {
        const res = await fetch('/api/users/me/mfa-reset', {
            method: 'POST',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function resetUserMfa(uuid: string): Promise<void> {
    try {
        const res = await fetch(`/api/users/${uuid}/mfa-reset`, {
            method: 'POST',
        });
        if (!res.ok) {
            const data = await res.json();
            throw APIError.fromResponse(data);
        }
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}
